import { PlayerScore } from './gameMode';

export class Scoreboard {
  private container: HTMLDivElement;
  private isVisible: boolean = false;
  private updateInterval: number | null = null;
  
  constructor() {
    this.container = this.createScoreboardElement();
    document.body.appendChild(this.container);
  }
  
  private createScoreboardElement(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'scoreboard';
    container.className = 'scoreboard';
    container.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: linear-gradient(135deg, rgba(10, 10, 20, 0.95), rgba(20, 20, 40, 0.95));
      border: 2px solid rgba(100, 200, 255, 0.5);
      border-radius: 15px;
      padding: 30px;
      min-width: 600px;
      max-width: 800px;
      max-height: 70vh;
      overflow-y: auto;
      font-family: 'Orbitron', 'Press Start 2P', monospace;
      color: #ffffff;
      box-shadow: 0 0 40px rgba(100, 200, 255, 0.3), inset 0 0 20px rgba(100, 200, 255, 0.1);
      display: none;
      z-index: 1000;
      backdrop-filter: blur(10px);
    `;
    
    // Add styles to head if not already present
    if (!document.getElementById('scoreboard-styles')) {
      const style = document.createElement('style');
      style.id = 'scoreboard-styles';
      style.textContent = `
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&display=swap');
        
        .scoreboard h2 {
          text-align: center;
          font-size: 32px;
          font-weight: 900;
          margin: 0 0 20px 0;
          color: #00ffff;
          text-transform: uppercase;
          letter-spacing: 3px;
          text-shadow: 0 0 20px rgba(0, 255, 255, 0.5);
        }
        
        .scoreboard-header {
          display: grid;
          grid-template-columns: 50px 1fr 80px 80px 80px 100px;
          padding: 15px 20px;
          background: rgba(100, 200, 255, 0.1);
          border-radius: 10px;
          margin-bottom: 10px;
          font-weight: 700;
          color: #64c8ff;
          text-transform: uppercase;
          font-size: 12px;
          letter-spacing: 1px;
        }
        
        .scoreboard-row {
          display: grid;
          grid-template-columns: 50px 1fr 80px 80px 80px 100px;
          padding: 12px 20px;
          margin: 5px 0;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 8px;
          transition: all 0.3s ease;
          border: 1px solid transparent;
          position: relative;
        }
        
        .scoreboard-row:hover {
          background: rgba(100, 200, 255, 0.1);
          border-color: rgba(100, 200, 255, 0.3);
          transform: translateX(5px);
          box-shadow: 0 0 20px rgba(100, 200, 255, 0.2);
        }
        
        .scoreboard-row.leader {
          background: linear-gradient(90deg, rgba(255, 215, 0, 0.2), rgba(255, 215, 0, 0.05));
          border-color: rgba(255, 215, 0, 0.5);
          box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
        }
        
        .scoreboard-row.leader::before {
          content: '👑';
          position: absolute;
          left: -25px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 20px;
        }
        
        .scoreboard-row.self {
          background: linear-gradient(90deg, rgba(0, 255, 0, 0.2), rgba(0, 255, 0, 0.05));
          border-color: rgba(0, 255, 0, 0.5);
        }
        
        .rank {
          font-weight: 900;
          font-size: 18px;
          color: #ffffff;
        }
        
        .rank.gold { color: #ffd700; text-shadow: 0 0 10px rgba(255, 215, 0, 0.5); }
        .rank.silver { color: #c0c0c0; text-shadow: 0 0 10px rgba(192, 192, 192, 0.5); }
        .rank.bronze { color: #cd7f32; text-shadow: 0 0 10px rgba(205, 127, 50, 0.5); }
        
        .player-name {
          font-weight: 700;
          font-size: 14px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .bot-badge {
          display: inline-block;
          background: rgba(255, 100, 100, 0.3);
          color: #ff6464;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          margin-left: 8px;
          text-transform: uppercase;
        }
        
        .score {
          font-weight: 900;
          font-size: 16px;
          color: #00ffff;
          text-align: center;
        }
        
        .kills, .deaths {
          text-align: center;
          font-weight: 700;
          font-size: 14px;
        }
        
        .kills { color: #64ff64; }
        .deaths { color: #ff6464; }
        
        .kd-ratio {
          text-align: center;
          font-weight: 900;
          font-size: 14px;
          color: #ffff64;
        }
        
        .scoreboard-footer {
          margin-top: 20px;
          padding-top: 15px;
          border-top: 1px solid rgba(100, 200, 255, 0.3);
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: #64c8ff;
        }
        
        .game-info {
          display: flex;
          gap: 20px;
        }
        
        .info-item {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        
        .info-label {
          opacity: 0.7;
          text-transform: uppercase;
        }
        
        .info-value {
          font-weight: 700;
          color: #00ffff;
        }
        
        .close-hint {
          opacity: 0.7;
          font-style: italic;
        }
      `;
      document.head.appendChild(style);
    }
    
    return container;
  }
  
  update(scores: PlayerScore[], currentPlayerId?: string, gameInfo?: {
    timeRemaining?: number;
    scoreLimit?: number;
    gameMode?: string;
  }) {
    let html = '<h2>🎮 Leaderboard 🎮</h2>';
    
    // Header
    html += `
      <div class="scoreboard-header">
        <div>Rank</div>
        <div>Player</div>
        <div>Score</div>
        <div>Kills</div>
        <div>Deaths</div>
        <div>K/D</div>
      </div>
    `;
    
    // Rows
    scores.forEach((score, index) => {
      const rank = index + 1;
      const rankClass = rank === 1 ? 'gold' : rank === 2 ? 'silver' : rank === 3 ? 'bronze' : '';
      const isLeader = rank === 1;
      const isSelf = score.id === currentPlayerId;
      const kd = score.deaths === 0 ? score.kills.toFixed(1) : (score.kills / score.deaths).toFixed(2);
      
      const rowClass = `scoreboard-row ${isLeader ? 'leader' : ''} ${isSelf ? 'self' : ''}`;
      
      html += `
        <div class="${rowClass}">
          <div class="rank ${rankClass}">#${rank}</div>
          <div class="player-name">
            ${score.name}
            ${score.isBot ? '<span class="bot-badge">BOT</span>' : ''}
          </div>
          <div class="score">${score.score}</div>
          <div class="kills">${score.kills}</div>
          <div class="deaths">${score.deaths}</div>
          <div class="kd-ratio">${kd}</div>
        </div>
      `;
    });
    
    // Footer with game info
    if (gameInfo) {
      html += '<div class="scoreboard-footer">';
      html += '<div class="game-info">';
      
      if (gameInfo.gameMode) {
        html += `
          <div class="info-item">
            <span class="info-label">Mode:</span>
            <span class="info-value">${gameInfo.gameMode}</span>
          </div>
        `;
      }
      
      if (gameInfo.timeRemaining !== undefined) {
        if (gameInfo.timeRemaining === -1) {
          // No time limit
          html += `
            <div class="info-item">
              <span class="info-label">Time:</span>
              <span class="info-value">No Limit</span>
            </div>
          `;
        } else if (gameInfo.timeRemaining >= 0) {
          const minutes = Math.floor(gameInfo.timeRemaining / 60);
          const seconds = gameInfo.timeRemaining % 60;
          html += `
            <div class="info-item">
              <span class="info-label">Time:</span>
              <span class="info-value">${minutes}:${seconds.toString().padStart(2, '0')}</span>
            </div>
          `;
        }
      }
      
      if (gameInfo.scoreLimit) {
        html += `
          <div class="info-item">
            <span class="info-label">Score Limit:</span>
            <span class="info-value">${gameInfo.scoreLimit}</span>
          </div>
        `;
      }
      
      html += '</div>';
      html += '<div class="close-hint">Press TAB to close</div>';
      html += '</div>';
    }
    
    this.container.innerHTML = html;
  }
  
  show() {
    this.container.style.display = 'block';
    this.isVisible = true;
  }
  
  hide() {
    this.container.style.display = 'none';
    this.isVisible = false;
  }
  
  toggle() {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }
  
  isOpen(): boolean {
    return this.isVisible;
  }
  
  destroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
    this.container.remove();
  }
}
